import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import api from '../../services/api';
import { ACTION_TYPES } from './config';

// Analitik Plan 4 (spec §6.5) — aksiyon açma/düzenleme (yalnız İK). Bulgular sayfası da kullanır: bulgudan açılan aksiyon
// `sourceInsightId` + önerilen türle gelir; tür ve kaynak bulgu düzenlemede değişmez (tekrar koruması anahtarı).
// Sorumlunun aktif bir kullanıcı hesabı olmalı — yoksa sunucu `performance.action.ownerHasNoUser` döner (yakalayıcı gösterir).

const employeeLabel = (e) => e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim();

export const employeeAsync = {
  onSearch: async (q) => {
    const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const r = res.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    return items.map((e) => ({ id: e.id, name: employeeLabel(e), profilePicture: e.profilePicture }));
  },
  onGetById: async (id) => {
    if (!id) return null;
    const res = await api.get(`/v1/employees/${id}`);
    const e = res.data?.data;
    return e ? { id: e.id, name: employeeLabel(e), profilePicture: e.profilePicture } : null;
  },
};

const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export default function ActionFormDrawer({ open, onClose, onSave, row, defaults }) {
  const { t } = useTranslation();
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get('/v1/evaluation-periods', { params: { pageSize: 200 } }).then((res) => {
      if (!res?.data?.status) return;
      const r = res.data.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      setPeriods(items.map((p) => ({ id: p.id, name: p.name })));
    });
  }, [open]);

  const editing = !!row;
  const fromInsight = !!(row?.sourceInsightId || defaults?.sourceInsightId);

  const schema = useMemo(() => [
    { name: 'title', label: t('performance.action.title'), type: 'text', required: true, maxLength: 200 },
    { name: 'ownerEmployeeId', label: t('performance.action.owner'), type: 'asyncSelect', required: true, onSearch: employeeAsync.onSearch, onGetById: employeeAsync.onGetById },
    { name: 'dueDate', label: t('performance.action.dueDate'), type: 'date', required: true, grid: 6 },
    {
      name: 'actionType',
      label: t('performance.action.actionType'),
      type: 'select',
      required: true,
      grid: 6,
      options: ACTION_TYPES.map((x) => ({ id: x, name: t(`performance.action.type.${x}`) })),
      hidden: fromInsight && editing,
    },
    { name: 'periodId', label: t('performance.action.period'), type: 'select', options: periods, hidden: fromInsight },
    { name: 'linkedEmployeeId', label: t('performance.action.linkedEmployee'), type: 'asyncSelect', onSearch: employeeAsync.onSearch, onGetById: employeeAsync.onGetById },
    { name: 'teamLabel', label: t('performance.action.teamLabel'), type: 'text', maxLength: 100 },
    { name: 'description', label: t('performance.action.description'), type: 'textarea', maxLength: 2000, rows: 4 },
  ], [t, periods, fromInsight, editing]);

  const initialValues = useMemo(() => {
    if (row) {
      return {
        title: row.title,
        ownerEmployeeId: row.ownerEmployeeId,
        dueDate: toDateInput(row.dueDate),
        actionType: row.actionType,
        periodId: row.periodId || null,
        linkedEmployeeId: row.linkedScopeType === 'employee' ? row.linkedScopeId : null,
        teamLabel: row.teamLabel || '',
        description: row.description || '',
      };
    }
    if (defaults) return { actionType: 'custom', ...defaults };
    return undefined;
  }, [row, defaults]);

  const handleSave = async (data) => {
    const payload = {
      title: data.title,
      ownerEmployeeId: data.ownerEmployeeId,
      dueDate: data.dueDate,
      actionType: data.actionType || 'custom',
      periodId: data.periodId || null,
      teamLabel: data.teamLabel || null,
      description: data.description || null,
      sourceInsightId: row?.sourceInsightId || defaults?.sourceInsightId || null,
      linkedScopeType: data.linkedEmployeeId ? 'employee' : null,
      linkedScopeId: data.linkedEmployeeId || null,
    };
    return onSave(payload);
  };

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={editing ? t('performance.action.editAction') : t('performance.action.newAction')}
      schema={schema}
      initialValues={initialValues}
      onSave={handleSave}
    />
  );
}
